import React, { useState } from 'react'
import { Alert, Button, CircularProgress } from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { Link } from 'react-router-dom'
import { Dropzone, useGetBusiness } from '@marketing-milk/frontend'
import {
  CREATIVE_IMPORT_LIMITS,
  CREATIVE_IMPORT_PACKAGING_PROMPT,
} from '@marketing-milk/interfaces'
import { CreativeImportState } from '../state'
import { useCreativeImportActive } from '../useCreativeImportActive'

interface Props {
  state: CreativeImportState
  onFile: (file: File) => void
}

const MB = 1024 * 1024

export const UploadStep = ({ state, onFile }: Props) => {
  const business = useGetBusiness()
  const archiveErrors = state.report?.archiveErrors ?? []
  const [promptCopied, setPromptCopied] = useState(false)
  // Warn-but-allow: a running import is a hint the user may be re-uploading
  // the same ZIP by accident (duplicate drafts — no dedup), never a block.
  const activeImport = useCreativeImportActive(true).data

  const copyPrompt = async () => {
    await navigator.clipboard.writeText(CREATIVE_IMPORT_PACKAGING_PROMPT)
    setPromptCopied(true)
    setTimeout(() => setPromptCopied(false), 2500)
  }

  return (
    <div className="flex flex-col gap-4">
      {activeImport && (
        <Alert severity="warning">
          An import is already running for this business.{' '}
          <Link
            to={`/${business.id}/creatives/import/${activeImport.creativeImportID}`}
            className="underline"
          >
            View its progress
          </Link>{' '}
          — or continue below to start another one.
        </Alert>
      )}
      {state.error && <Alert severity="error">{state.error}</Alert>}
      {archiveErrors.map(error => (
        <Alert key={error.code} severity="error">
          {error.message}
        </Alert>
      ))}

      {state.validating ? (
        <div className="flex flex-col items-center py-12 gap-3">
          <CircularProgress />
          <p className="text-gray-700">Validating archive…</p>
        </div>
      ) : (
        <Dropzone
          allowedExtensions={['.zip']}
          inputProps={{ 'data-testid': 'creative-import-dropzone' }}
          handleUpload={(file: File) => onFile(file)}
        />
      )}

      <div className="text-sm text-gray-700">
        <p className="font-medium mb-1">Expected archive layout</p>
        <pre className="bg-gray-100 rounded p-3 mb-2 overflow-x-auto">
          {
            'creatives.json       ← manifest listing the media to import\nmedia/\n  lion.png\n  cheetah.jpg\n  hunt.mp4'
          }
        </pre>
        <p>
          The manifest selects exactly which files become draft creatives — unlisted files are
          ignored. Copy fields (headline, description, …) are optional; drafts import incomplete and
          are finished in Builder. Limits: up to {CREATIVE_IMPORT_LIMITS.maxRows} creatives and{' '}
          {CREATIVE_IMPORT_LIMITS.maxMediaTotal} files per import, .jpg/.png images up to{' '}
          {Math.round(CREATIVE_IMPORT_LIMITS.imageMaxBytes / MB)} MB each and .mp4/.mov/.gif video
          up to {Math.round(CREATIVE_IMPORT_LIMITS.videoMaxBytes / MB)} MB each.
        </p>
        <p className="mt-3">
          Made your images with an AI? Paste this prompt into that chat and it will package the
          folder, manifest, and ZIP for you:
        </p>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ContentCopyIcon />}
          onClick={copyPrompt}
          className="mt-1"
        >
          {promptCopied ? 'Copied!' : 'Copy prompt for your AI'}
        </Button>
      </div>
    </div>
  )
}
